import Api from 'api';
import { ReactComponent as CheckmarkIcon } from 'assets/icons/checkmark.svg';
import { ReactComponent as EditIcon } from 'assets/icons/edit2.svg';
import { ReactComponent as TrashIcon } from 'assets/icons/trash.svg';
import Button from 'atoms/Button';
import Container from 'atoms/Container';
import { useEffect, useState } from 'react';

import ContentBox from 'atoms/ContentBox';
import Grid from 'atoms/Grid';
import ListHeader from 'atoms/ListHeader/ListHeader';
import Modal from 'atoms/Modal';
import SkeletonLoader from 'atoms/SkeletonLoader';

type Automation = { id: string; name: string; status: 'OFF' | 'ON' };

export const AutomationConfigPage = () => {
    const [automations, setAutomations] = useState<Automation[]>([]);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [deleteModalOpen, setDeleteModalOpen] = useState<boolean>(false);
    const [automationToDelete, setAutomationToDelete] = useState<Automation | null>(null);

    useEffect(() => {
        Api.defaultApi()
            .fetchAutomations()
            .then((response) => {
                setAutomations(response as Automation[]);
                setIsLoading(false);
            })
            .catch(() => {
                setIsLoading(false);
            });
    }, []);

    const handleDeleteClick = (automation: Automation) => {
        setAutomationToDelete(automation);
        setDeleteModalOpen(true);
    };

    const confirmDelete = () => {
        if (!automationToDelete) return;
        
        Api.defaultApi()
            .deleteAutomation(automationToDelete.id)
            .then(() => {
                setAutomations(
                    automations.filter((automation) => automation.id !== automationToDelete.id)
                );
                setDeleteModalOpen(false);
                setAutomationToDelete(null);
            })
            .catch(() => {
                setDeleteModalOpen(false);
                setAutomationToDelete(null);
            });
    };

    const cancelDelete = () => {
        setDeleteModalOpen(false);
        setAutomationToDelete(null);
    };

    return (
        <>
            <Container>
                <ListHeader text="Configure Automation">
                    {automations.length > 0 && (
                        <Button size="m" theme="blank" to="/automation">
                            <CheckmarkIcon /> <p>Done</p>
                        </Button>
                    )}
                </ListHeader>
            </Container>
            <Container narrow>
                <Grid columns={1} gap={12}>
                    {isLoading ? (
                        <>
                            <SkeletonLoader />
                            <SkeletonLoader />
                        </>
                    ) : (
                        <>
                            {automations.length === 0 && (
                                <div
                                    style={{
                                        textAlign: 'center',
                                        padding: '60px 20px',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                        background: 'var(--box-background)',
                                        borderRadius: 'var(--radius)',
                                        border: '1px solid var(--grey)',
                                        marginBottom: '24px',
                                    }}
                                >
                                    <div
                                        style={{
                                            width: '80px',
                                            height: '80px',
                                            borderRadius: '50%',
                                            background:
                                                'linear-gradient(135deg, #9B59B6, #8E44AD)',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            marginBottom: '24px',
                                            fontSize: '32px',
                                        }}
                                    >
                                        ⚙️
                                    </div>
                                    <h3
                                        style={{
                                            margin: '0 0 12px 0',
                                            fontSize: '20px',
                                            fontWeight: '600',
                                            color: 'var(--text)',
                                        }}
                                    >
                                        No automations yet
                                    </h3>
                                    <p
                                        style={{
                                            margin: '0 0 24px 0',
                                            fontSize: '16px',
                                            lineHeight: '1.5',
                                            color: 'var(--text)',
                                            opacity: 0.7,
                                            maxWidth: '400px',
                                        }}
                                    >
                                        Create your first automation to handle
                                        recurring tasks automatically.
                                    </p>
                                </div>
                            )}

                            {automations.length > 0 &&
                                automations.map((automation, index) => (
                                    <ContentBox key={index}>
                                        <div
                                            style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'space-between',
                                                padding: '12px 0',
                                                minHeight: '48px',
                                            }}
                                        >
                                            <div
                                                style={{
                                                    fontSize: '15px',
                                                    fontWeight: '500',
                                                    color: 'var(--text)',
                                                    flex: 1,
                                                    minWidth: 0,
                                                }}
                                            >
                                                {automation.name}
                                            </div>
                                            <div
                                                style={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: '4px',
                                                    background:
                                                        automation.status ===
                                                        'ON'
                                                            ? 'rgba(34, 197, 94, 0.1)'
                                                            : 'rgba(156, 163, 175, 0.1)',
                                                    color:
                                                        automation.status ===
                                                        'ON'
                                                            ? '#22c55e'
                                                            : '#9ca3af',
                                                    padding: '3px 6px',
                                                    borderRadius: '8px',
                                                    fontSize: '10px',
                                                    fontWeight: '600',
                                                    textTransform: 'uppercase',
                                                    letterSpacing: '0.5px',
                                                    whiteSpace: 'nowrap',
                                                }}
                                            >
                                                <div
                                                    style={{
                                                        width: '4px',
                                                        height: '4px',
                                                        borderRadius: '50%',
                                                        background:
                                                            automation.status ===
                                                            'ON'
                                                                ? '#22c55e'
                                                                : '#9ca3af',
                                                    }}
                                                ></div>
                                                {automation.status}
                                            </div>
                                            <div
                                                style={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: '6px',
                                                    marginLeft: '16px',
                                                }}
                                            >
                                                {
                                                    false && <Button
                                                        size="s"
                                                        theme="plain"
                                                        /*to={`/automation/${automation.id}/edit`}*/
                                                        style={{
                                                            padding: '4px 8px',
                                                            fontSize: '11px',
                                                            whiteSpace:
                                                                'nowrap',
                                                        }}
                                                    >
                                                        <EditIcon />
                                                    </Button>
                                                }
                                                <Button
                                                    size="s"
                                                    theme="plain"
                                                    onClick={() =>
                                                        handleDeleteClick(
                                                            automation
                                                        )
                                                    }
                                                    style={{
                                                        padding: '4px 8px',
                                                        fontSize: '11px',
                                                        whiteSpace: 'nowrap',
                                                        color: '#ef4444',
                                                    }}
                                                >
                                                    <TrashIcon />
                                                </Button>
                                            </div>
                                        </div>
                                    </ContentBox>
                                ))}
                        </>
                    )}
                    <div
                        style={{
                            display: 'flex',
                            gap: '12px',
                            justifyContent: 'center',
                            marginTop: '24px',
                        }}
                    >
                        <Button
                            size="m"
                            theme="primary"
                            to="/automation/configure/create"
                            style={{
                                fontSize: '14px',
                                padding: '12px 24px',
                            }}
                        >
                            + New automation
                        </Button>
                        <Button
                            size="m"
                            theme="blank"
                            to="/automation"
                            style={{
                                fontSize: '14px',
                                padding: '12px 24px',
                            }}
                        >
                            Go Back
                        </Button>
                    </div>
                </Grid>
            </Container>
            <Modal open={deleteModalOpen} onClose={cancelDelete}>
                <div style={{ textAlign: 'center', padding: '20px' }}>
                    <h3 style={{ marginBottom: '16px' }}>Delete Automation</h3>
                    <p style={{ marginBottom: '24px' }}>
                        Are you sure you want to delete "
                        {automationToDelete?.name}"? This action cannot be
                        undone.
                    </p>
                    <div
                        style={{
                            display: 'flex',
                            gap: '12px',
                            justifyContent: 'center',
                        }}
                    >
                        <Button size="m" theme="blank" onClick={cancelDelete}>
                            Cancel
                        </Button>
                        <Button
                            size="m"
                            theme="primary"
                            onClick={confirmDelete}
                        >
                            Delete
                        </Button>
                    </div>
                </div>
            </Modal>
        </>
    );
};
